import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@zep-us/zus';
import { ArrowTurnDownrightIcon } from '@zep-us/zus/icons';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { type AiBuilderJumpLink } from '../store';
const AI_BUILDER_JUMP_LINK_COLLAPSED_MAX_ROWS = 3;
const AI_BUILDER_JUMP_LINK_ROW_TOP_TOLERANCE = 2;
export const getAiBuilderCollapsedJumpLinkState = (chipRowTops: number[], maxRows = AI_BUILDER_JUMP_LINK_COLLAPSED_MAX_ROWS) => {
  const rowTops: number[] = [];
  for (const [index, top] of chipRowTops.entries()) {
    const rowIndex = getAiBuilderJumpLinkRowIndex(rowTops, top);
    if (rowIndex >= maxRows) {
      return {
        hasOverflow: true,
        visibleCount: index
      };
    }
  }
  return {
    hasOverflow: false,
    visibleCount: chipRowTops.length
  };
};
const getAiBuilderJumpLinkRowIndex = (rowTops: number[], top: number) => {
  const existingRowIndex = rowTops.findIndex(rowTop => Math.abs(rowTop - top) <= AI_BUILDER_JUMP_LINK_ROW_TOP_TOLERANCE);
  if (existingRowIndex >= 0) return existingRowIndex;
  rowTops.push(top);
  rowTops.sort((a, b) => a - b);
  return rowTops.findIndex(rowTop => rowTop === top);
};
export const AiBuilderJumpLinkList = ({
  jumpLinks,
  onJumpLinkClick
}: {
  jumpLinks: AiBuilderJumpLink[];
  onJumpLinkClick?: (quizNumber: number) => void;
}) => {
  const copy = useAiBuilderChatCopy();
  const measureListRef = useRef<HTMLDivElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [collapsedVisibleCount, setCollapsedVisibleCount] = useState(() => jumpLinks.length);
  const [hasCollapsedOverflow, setHasCollapsedOverflow] = useState(false);
  const jumpLinkSignature = useMemo(() => jumpLinks.map(link => `${link.quizNumber}:${link.label}`).join('|'), [jumpLinks]);
  const visibleJumpLinks = expanded ? jumpLinks : jumpLinks.slice(0, collapsedVisibleCount);
  const shouldShowMoreButton = !expanded && hasCollapsedOverflow;
  const measureCollapsedRows = useCallback(() => {
    const measureList = measureListRef.current;
    if (!measureList) return;
    const measureListTop = measureList.getBoundingClientRect().top;
    const chipRowTops = Array.from(measureList.querySelectorAll<HTMLElement>('[data-ai-assistant-jump-link-measure-chip]'), chip => Math.round(chip.getBoundingClientRect().top - measureListTop));
    const collapsedState = getAiBuilderCollapsedJumpLinkState(chipRowTops);
    setCollapsedVisibleCount(collapsedState.visibleCount);
    setHasCollapsedOverflow(collapsedState.hasOverflow);
  }, []);
  useLayoutEffect(() => {
    setExpanded(false);
  }, [jumpLinkSignature]);
  useLayoutEffect(() => {
    measureCollapsedRows();
  }, [jumpLinkSignature, measureCollapsedRows]);
  useEffect(() => {
    const measureList = measureListRef.current;
    if (!measureList || typeof ResizeObserver === 'undefined') return;
    const resizeObserver = new ResizeObserver(measureCollapsedRows);
    resizeObserver.observe(measureList);
    return () => {
      resizeObserver.disconnect();
    };
  }, [measureCollapsedRows]);
  return <div className="relative flex w-full flex-col items-start gap-sm" data-sentry-component="AiBuilderJumpLinkList" data-sentry-source-file="AiBuilderJumpLinkList.tsx">
      <div className="flex min-w-0 flex-1 flex-wrap gap-xs" data-ai-assistant-jump-links>
        {visibleJumpLinks.map(link => <AiBuilderJumpLinkButton key={link.quizNumber} link={link} jumpToQuestionLabel={copy.jumpLinks.jumpToQuestion(link.label)} onClick={() => onJumpLinkClick?.(link.quizNumber)} />)}
      </div>

      {shouldShowMoreButton ? <Button type="button" variant="contained" intent="neutral" size="sm" rounded="full" aria-label={copy.jumpLinks.moreAriaLabel} onClick={() => setExpanded(true)}>
          {copy.jumpLinks.more}
        </Button> : null}

      <div ref={measureListRef} aria-hidden="true" className="pointer-events-none invisible absolute left-0 top-0 flex w-full flex-wrap gap-xs" data-ai-assistant-jump-link-measure-list>
        {jumpLinks.map(link => <AiBuilderJumpLinkButton key={link.quizNumber} link={link} jumpToQuestionLabel={copy.jumpLinks.jumpToQuestion(link.label)} tabIndex={-1} data-ai-assistant-jump-link-measure-chip />)}
      </div>
    </div>;
};
const AiBuilderJumpLinkButton = ({
  jumpToQuestionLabel,
  link,
  ...props
}: {
  jumpToQuestionLabel: string;
  link: AiBuilderJumpLink;
  onClick?: () => void;
  tabIndex?: number;
  'data-ai-assistant-jump-link-measure-chip'?: boolean;
}) => <Button type="button" variant="outlined" intent="neutral" size="sm" rounded="full" className="bg-default" leftSlot={<ArrowTurnDownrightIcon size="xs" fill="currentColor" />} aria-label={jumpToQuestionLabel} title={link.label} {...props} data-sentry-element="Button" data-sentry-component="AiBuilderJumpLinkButton" data-sentry-source-file="AiBuilderJumpLinkList.tsx">
    {link.label}
  </Button>;